import { useMutation } from '@tanstack/react-query';
import { projectService, UpdateProjectPayload } from '@services/project';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { PROJECTS_BY_STATE_QUERY_KEY } from './useProjectsByState';
import { PROJECTS_QUERY_KEY } from './useProjects';
import { PROJECT_QUERY_KEY } from './useProject';
import { PENDING_INVOICES_COUNT_QUERY_KEY } from '@hooks/api/activity/usePendingInvoicesCount';
import { Project } from '@models/project.model';
import { useTranslation } from '@hooks/useTranslation';

export const useEditProject = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['project'],
    mutationFn: async (payload: UpdateProjectPayload) => {
      return await projectService.updateProject(payload);
    },
    onSuccess: async (data: Project) => {
      toast.success(tActionMessages.projectUpdatedSuccessfully);

      // Invalidate all project-related queries to trigger refetch
      await queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [PROJECTS_BY_STATE_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEY] });

      // Completing a project makes the backend create INVOICE_PENDING activities for any
      // tasks with un-invoiced items. Refresh the activity feeds and the dashboard invoice
      // triage pill so they reflect the new activities without a manual page reload.
      if (String(data?.state ?? '').toUpperCase() === 'COMPLETED') {
        await queryClient.invalidateQueries({ queryKey: ['activities'] });
        await queryClient.invalidateQueries({ queryKey: [PENDING_INVOICES_COUNT_QUERY_KEY] });
      }

      // Wait for queries to refetch
      await queryClient.refetchQueries({ queryKey: [PROJECTS_BY_STATE_QUERY_KEY] });
    },
    onError: (error: any) => {
      console.error('Edit project error:', error);
      const errorMessage =
        error?.response?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        error?.toString() ||
        'Failed to update project';
      toast.error(errorMessage);
    },
  });
};

export default useEditProject;
